import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import DeleteAccountDialog from 'src/presentation/dialogs/DeleteAccountDialog';
import ResetPasswordDialog from 'src/presentation/dialogs/ResetPasswordDialog';
import ErrorView from 'src/presentation/components/organisms/ErrorView';
import InfoItem from 'src/presentation/components/molecules/InfoItem';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import { useProfileStore } from 'src/domain/stores/useProfileStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Styles from 'src/core/utils/Styles';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import { SvgIconType } from '@thermenresorts/icons';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { AccountNavigation, AuthNavigation } from 'src/presentation/navigation/types/NavigationTypes';
import Fonts from 'src/core/utils/Fonts';
import Constants from 'src/core/utils/Constants';
import type ApiProfileField from 'src/core/models/api/ApiProfileField';
import Translations from 'src/core/utils/Translations';

const profileFieldValue = (field: ApiProfileField): string | undefined => {
	const displayValue = field.display_value ?? '';

	return displayValue === '' ? (field.value ?? undefined) : displayValue;
};

export default () => {
	const tabBarInset = useTabBarInset();
	const { user, deleteAccount, setLanguage, postApiForgotPassword } = useAuthStore();
	const {
		profile,
		isLoading,
		errorMessage,
		isDeleteAccountDialogVisible,
		isDeletingAccount,
		deleteAccountPassword,
		deleteAccountError,
		isDeleteAccountPasswordVisible,
		isResetPasswordDialogVisible,
		isResettingPassword,
		resetPasswordSuccessful,
		init,
		getApiProfile,
		setDeleteAccountDialogVisible,
		setDeletingAccount,
		setDeleteAccountPassword,
		setDeleteAccountPasswordVisible,
		applyDeleteAccountError,
		setResetPasswordDialogVisible,
		setResettingPassword,
		setResetPasswordSuccessful,
	} = useProfileStore();
	const { logEvent, captureEvent } = useServicesStore();
	const navigation = useNavigation<StackNavigationProp<AccountNavigation>>();
	const authNavigation = useNavigation<StackNavigationProp<AuthNavigation>>();

	useAppStateScreenListener({
		onInit: () => init(),
		onFocussed: () => getApiProfile(),
		onForegrounded: () => getApiProfile(),
	});

	const doDeleteAccount = async () => {
		setDeletingAccount(true);

		await deleteAccount({
			password: deleteAccountPassword,
			onSuccess: async () => {
				logEvent({
					name: Constants.analyticsEventDeleteAccount,
					params: { [Constants.analyticsParamScreenPage]: Constants.screenProfile },
					user,
				});

				await setLanguage(Constants.localeNl);

				setDeleteAccountDialogVisible(false);
				authNavigation.replace(Constants.screenLogin);
			},
			onError: applyDeleteAccountError,
		});

		setDeletingAccount(false);
	};

	const resetPassword = async () => {
		setResettingPassword(true);

		await postApiForgotPassword({
			email: profile?.login_data.fields.email.value ?? '',
			onSuccess: (response) => {
				logEvent({
					name: Constants.analyticsEventChangeAccountSettings,
					params: {
						[Constants.analyticsParamScreenPage]: Constants.screenProfile,
						[Constants.analyticsParamSelectItem]: Constants.analyticsValueSelectItemPassword,
					},
					user,
				});

				setResetPasswordSuccessful(response.status === Constants.httpStatusOk);
				setResettingPassword(false);
			},
			onError: (error) => {
				setResetPasswordSuccessful(false);
				setResettingPassword(false);
				captureEvent({ message: 'postApiForgotPassword failed', extra: { error } });
			},
		});
	};

	return (
		<View style={Styles.screen.white}>
			{isLoading ? (
				<LoadingIndicator style={{ flex: Dimens.flex1 }} />
			) : errorMessage ? (
				<ErrorView onPress={() => getApiProfile()} />
			) : (
				<View style={Styles.screen.white}>
					<DeleteAccountDialog
						errorPassword={deleteAccountError}
						isLoading={isDeletingAccount}
						isPasswordVisible={isDeleteAccountPasswordVisible}
						password={deleteAccountPassword}
						setPassword={setDeleteAccountPassword}
						setPasswordVisible={setDeleteAccountPasswordVisible}
						text={Translations.areYouSureYouWantToDeleteYourAccount}
						title={Translations.deleteAccount}
						visible={isDeleteAccountDialogVisible}
						onPress={() => setDeleteAccountDialogVisible(false)}
						onPressClaim={() => doDeleteAccount()}
						onPressForgotPassword={() => {
							setDeleteAccountDialogVisible(false);
							setResetPasswordSuccessful(null);
							setResetPasswordDialogVisible(true);
						}}
					/>
					<ResetPasswordDialog
						buttonText={Translations.accountCloseModal}
						hideResetButton={resetPasswordSuccessful !== null}
						isLoading={isResettingPassword}
						resetText={Translations.accountResetPassword}
						text={
							resetPasswordSuccessful ? Translations.accountResetDescriptionSuccess : Translations.accountResetDescription
						}
						title={resetPasswordSuccessful ? Translations.accountResetConfirmed : Translations.accountResetConfirm}
						visible={isResetPasswordDialogVisible}
						onPress={() => setResetPasswordDialogVisible(false)}
						onPressReset={() => resetPassword()}
					/>
					<ScrollView contentContainerStyle={{ paddingBottom: tabBarInset }}>
						<View style={[styles.settingBlock, styles.borderBottom]}>
							<View style={Styles.screen.column}>
								<Text style={styles.sectionTitle}>{profile?.personal_data.label}</Text>
								{profile &&
									Object.entries(profile.personal_data.fields).map(([key, field], idx, fields) => {
										return (
											<InfoItem key={key} label={field.label} last={idx === fields.length - 1} value={profileFieldValue(field)} />
										);
									})}
								<View style={{ height: Dimens.size20 }} />
								<Pressable onPress={() => navigation.navigate(Constants.screenEditProfile)}>
									<View style={Styles.row.iconLabel}>
										<SvgIcon name={SvgIconType.edit} size={Dimens.iconSize16} tintColor={Colors.black} />
										<Text style={styles.actionLabel}>{profile?.personal_data.button_label}</Text>
									</View>
								</Pressable>
							</View>
						</View>
						<View style={[styles.settingBlock, styles.borderBottom]}>
							<View style={Styles.screen.column}>
								<Text style={styles.sectionTitle}>{profile?.address_data.label}</Text>
								{profile &&
									Object.entries(profile.address_data.fields).map(([key, field], idx, fields) => {
										return (
											<InfoItem key={key} label={field.label} last={idx === fields.length - 1} value={profileFieldValue(field)} />
										);
									})}
								<View style={{ height: Dimens.size20 }} />
								<Pressable onPress={() => navigation.navigate(Constants.screenEditAddress)}>
									<View style={Styles.row.iconLabel}>
										<SvgIcon name={SvgIconType.edit} size={Dimens.iconSize16} tintColor={Colors.black} />
										<Text style={styles.actionLabel}>{profile?.address_data.button_label}</Text>
									</View>
								</Pressable>
							</View>
						</View>
						<View style={[styles.settingBlock, styles.borderBottom]}>
							<View style={Styles.screen.column}>
								<Text style={styles.sectionTitle}>{profile?.login_data.label}</Text>
								<InfoItem
									label={profile?.login_data.fields.email.label}
									value={profile?.login_data.fields.email.value ?? undefined}
								/>
								<InfoItem
									label={profile?.login_data.fields.password.label}
									last={true}
									value={profile?.login_data.fields.password.value ?? undefined}
								/>
								<View style={{ height: Dimens.size20 }} />
								<Pressable
									onPress={() => {
										setResetPasswordSuccessful(null);
										setResetPasswordDialogVisible(true);
									}}
								>
									<View style={Styles.row.iconLabel}>
										<SvgIcon name={SvgIconType.edit} size={Dimens.iconSize16} tintColor={Colors.black} />
										<Text style={styles.actionLabel}>{Translations.accountResetPassword}</Text>
									</View>
								</Pressable>
							</View>
						</View>
						<Text style={styles.deleteWarning}>
							<Text style={styles.deleteWarningEmphasis}>{Translations.accountAttention}</Text>
							<Text style={styles.deleteWarningText}>{' ' + Translations.accountDeleteWarning}</Text>
						</Text>
						<View style={[styles.settingBlock]}>
							<View style={Styles.screen.column}>
								<Pressable onPress={() => setDeleteAccountDialogVisible(true)}>
									<View style={Styles.row.iconLabel}>
										<SvgIcon name={SvgIconType.trashAlt} size={Dimens.iconSize16} tintColor={Colors.primary} />
										<Text style={styles.deleteLabel}>{profile?.remove_profile_button_label}</Text>
									</View>
								</Pressable>
							</View>
						</View>
					</ScrollView>
				</View>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	sectionTitle: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize20,
		lineHeight: Dimens.lineHeight30,
		color: Colors.gray750,
		marginBottom: Dimens.padding24,
	},
	actionLabel: {
		fontSize: Dimens.fontSize15,
		lineHeight: Dimens.lineHeight25,
		color: Colors.black,
	},
	deleteWarning: {
		fontFamily: Fonts.barlow,
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight18,
		color: Colors.gray800,
		marginHorizontal: Dimens.padding18,
		marginTop: Dimens.padding32,
	},
	deleteWarningEmphasis: {
		fontFamily: Fonts.barlowMedium,
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight18,
		color: Colors.gray800,
	},
	deleteWarningText: {
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight18,
		color: Colors.gray800,
	},
	deleteLabel: {
		fontSize: Dimens.fontSize15,
		lineHeight: Dimens.lineHeight25,
		color: Colors.primary,
	},
	settingBlock: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingVertical: Dimens.padding24,
		marginHorizontal: Dimens.padding18,
	},
	borderBottom: {
		borderColor: Colors.gray200,
		borderBottomWidth: Dimens.borderWidth1,
	},
});
